/**
 * Compound interest — pure, deterministic lump-sum growth (rounded to cents).
 * A = P(1 + r/n)^(n·t). Safe to render server-side.
 */

export interface CompoundResult {
  /** Final balance after compounding. */
  finalBalance: number;
  /** Interest earned (finalBalance − principal). */
  totalInterest: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function compute(
  principal: number,
  annualRatePercent: number,
  years: number,
  compoundsPerYear: number,
): CompoundResult | null {
  if (
    ![principal, annualRatePercent, years, compoundsPerYear].every((x) =>
      Number.isFinite(x),
    )
  ) {
    return null;
  }
  if (
    principal <= 0 ||
    annualRatePercent < 0 ||
    years <= 0 ||
    compoundsPerYear < 1
  ) {
    return null;
  }

  const n = Math.floor(compoundsPerYear);
  const r = annualRatePercent / 100;
  const balance = principal * (1 + r / n) ** (n * years);

  return {
    finalBalance: round2(balance),
    totalInterest: round2(balance - principal),
  };
}
